
import React, { useState, useMemo } from 'react';
import { GeneratedImage } from '../types';
import { ICONS } from '../constants';
import clsx from 'clsx';
import { motion, AnimatePresence } from 'framer-motion';

interface HistoryProps {
  items: GeneratedImage[];
  onRemove: (id: string) => void;
  onBulkRemove?: (ids: string[]) => void;
  onClose: () => void;
  onView?: (url: string) => void;
  onPushProcess?: (item: GeneratedImage) => void;
  onItemUpdate?: () => void;
  onToggleFavorite: (id: string, isFav: boolean) => void;
  onBulkFavorite?: (ids: string[], isFav: boolean) => void;
  onDownload: (url: string) => void;
}

// Grouping Helper
const groupItemsByDate = (items: GeneratedImage[]) => {
    const groups: { [key: string]: GeneratedImage[] } = {};
    const today = new Date().toDateString();
    const yesterday = new Date(Date.now() - 86400000).toDateString();

    items.forEach(item => {
        const date = new Date(item.timestamp).toDateString();
        let label = date;
        if (date === today) label = "Today";
        else if (date === yesterday) label = "Yesterday";
        else {
            label = new Date(item.timestamp).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
        }

        if (!groups[label]) groups[label] = [];
        groups[label].push(item);
    });
    return groups;
};

const History: React.FC<HistoryProps> = ({ 
    items, 
    onRemove, 
    onBulkRemove,
    onClose, 
    onView, 
    onPushProcess, 
    onToggleFavorite,
    onBulkFavorite,
    onDownload
}) => {
  const [filter, setFilter] = useState<'ALL' | 'FAVORITES'>('ALL');
  
  // Selection State
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const filteredItems = useMemo(() => {
    return items.filter(item => {
        if (filter === 'FAVORITES') return item.isFavorite;
        return true;
    });
  }, [items, filter]);

  const groupedItems = useMemo(() => groupItemsByDate(filteredItems), [filteredItems]);
  const groupKeys = Object.keys(groupedItems);

  const toggleSelection = (id: string) => {
      setSelectedIds(prev => {
          const next = new Set(prev);
          if (next.has(id)) next.delete(id);
          else next.add(id);
          return next;
      });
  };

  const handleSelectAll = () => {
      if (selectedIds.size === filteredItems.length) {
          setSelectedIds(new Set()); // Deselect all
      } else {
          setSelectedIds(new Set(filteredItems.map(i => i.id)));
      }
  };

  const handleBulkDelete = () => {
      if (selectedIds.size === 0) return;
      if (confirm(`Delete ${selectedIds.size} items?`)) {
          if (onBulkRemove) onBulkRemove(Array.from(selectedIds));
          setSelectedIds(new Set());
          setIsSelectionMode(false);
      }
  };

  const handleBulkFav = (isFav: boolean) => {
      if (selectedIds.size === 0) return;
      if (onBulkFavorite) onBulkFavorite(Array.from(selectedIds), isFav);
      setSelectedIds(new Set());
      setIsSelectionMode(false);
  };

  const exitSelectionMode = () => {
      setIsSelectionMode(false);
      setSelectedIds(new Set());
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black flex flex-col animate-in fade-in duration-500" role="dialog" aria-label="History Gallery">
       
       {/* High-End Frosted Header */}
       <div className="h-16 border-b border-white/5 flex items-center justify-between px-4 md:px-6 bg-black/90 backdrop-blur-xl sticky top-0 z-50">
           
           <div className="flex items-center gap-4">
                {/* Close Button */}
                <button 
                    onClick={onClose} 
                    className="p-2 text-zinc-500 hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-white rounded-full bg-zinc-900 md:bg-transparent"
                    aria-label="Close Gallery"
                >
                    <ICONS.Close className="w-5 h-5" />
                </button>

                {/* Left: Filter Toggles (Hidden in selection mode for clarity) */}
                {!isSelectionMode && (
                    <div className="flex bg-zinc-900/50 rounded-lg p-1 border border-white/5" role="tablist">
                        <button 
                            onClick={() => setFilter('ALL')}
                            role="tab"
                            aria-selected={filter === 'ALL'}
                            className={clsx(
                                "px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider rounded-md transition-all focus:outline-none focus:ring-2 focus:ring-white",
                                filter === 'ALL' ? "bg-white text-black shadow-sm" : "text-zinc-500 hover:text-zinc-300"
                            )}
                        >
                            All
                        </button>
                        <button 
                            onClick={() => setFilter('FAVORITES')}
                            role="tab"
                            aria-selected={filter === 'FAVORITES'}
                            aria-label="Show Favorites Only"
                            className={clsx(
                                "px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider rounded-md transition-all flex items-center gap-2 focus:outline-none focus:ring-2 focus:ring-white",
                                filter === 'FAVORITES' ? "bg-white text-black shadow-sm" : "text-zinc-500 hover:text-zinc-300"
                            )}
                        >
                            <ICONS.Heart className="w-3 h-3" />
                        </button>
                    </div>
                )}
           </div>

           {/* Right: Selection Controls */}
           <div className="flex items-center gap-3">
               
               {isSelectionMode ? (
                   <>
                        <span className="text-xs font-mono text-zinc-400 hidden md:inline">{selectedIds.size} Selected</span>
                        <button 
                            onClick={handleSelectAll}
                            className="text-[10px] uppercase font-bold tracking-wider text-white hover:text-zinc-300 px-3 py-2 focus:outline-none focus:underline hidden md:block"
                        >
                            {selectedIds.size === filteredItems.length ? "Deselect All" : "Select All"}
                        </button>
                        <button 
                            onClick={exitSelectionMode}
                            className="text-[10px] uppercase font-bold tracking-wider text-white bg-zinc-800 hover:bg-zinc-700 px-4 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-white"
                        >
                            Done
                        </button>
                   </>
               ) : (
                   <button 
                        onClick={() => setIsSelectionMode(true)}
                        className="text-[10px] uppercase font-bold tracking-wider text-black bg-white hover:bg-zinc-200 px-4 py-2 rounded-md flex items-center gap-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                        <ICONS.Check className="w-3 h-3" /> Select
                    </button>
               )}
           </div>
       </div>

       {/* Main Scroll Area */}
       <div className="flex-1 overflow-y-auto bg-black p-2 md:p-8 scrollbar-hide select-none pb-32">
            <div className="max-w-[1600px] mx-auto">
               
               {filteredItems.length === 0 ? (
                   <div className="flex flex-col items-center justify-center py-40 opacity-50">
                       <ICONS.Layers className="w-12 h-12 mb-4 stroke-[0.5]" aria-hidden="true" />
                       <p className="text-xs uppercase tracking-[0.2em] font-medium">No Media Found</p>
                   </div>
               ) : (
                   <div className="space-y-8">
                       {groupKeys.map(dateLabel => (
                           <section key={dateLabel}>
                               {/* Sticky-ish Date Header */}
                               <h3 className="text-sm md:text-base font-medium text-zinc-400 mb-4 sticky top-0 z-10 inline-block bg-black/50 backdrop-blur-sm pr-4 py-1 rounded-r-md">
                                   {dateLabel} <span className="text-xs text-zinc-600 font-mono ml-2 opacity-50">{groupedItems[dateLabel].length}</span>
                               </h3>
                               
                               {/* Grid */}
                               <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-1 md:gap-4" role="grid">
                                   {groupedItems[dateLabel].map(item => {
                                       const isSelected = selectedIds.has(item.id);
                                       return (
                                           <div 
                                                key={item.id} 
                                                role="gridcell"
                                                tabIndex={0}
                                                className={clsx(
                                                    "relative group aspect-square bg-zinc-900 overflow-hidden cursor-pointer transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-black",
                                                    isSelectionMode && isSelected ? "ring-2 ring-white ring-offset-2 ring-offset-black z-10 scale-95 opacity-100" : "",
                                                    isSelectionMode && !isSelected ? "opacity-50 hover:opacity-100 scale-90" : ""
                                                )}
                                                onClick={() => {
                                                    if (isSelectionMode) toggleSelection(item.id);
                                                    else if (onView) onView(item.url);
                                                }}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                        e.preventDefault();
                                                        if (isSelectionMode) toggleSelection(item.id);
                                                        else if (onView) onView(item.url);
                                                    }
                                                }}
                                                aria-label={`Image generated on ${new Date(item.timestamp).toLocaleDateString()}`}
                                                aria-selected={isSelected}
                                           >
                                               <img 
                                                    src={item.url} 
                                                    alt={item.label || "Generated Image"} 
                                                    className="w-full h-full object-cover" 
                                                    loading="lazy" 
                                                />
                                               
                                               {/* Selection Overlay Checkmark */}
                                               {isSelectionMode && (
                                                   <div className={clsx(
                                                       "absolute top-2 right-2 w-5 h-5 rounded-full flex items-center justify-center border transition-colors shadow-lg z-20",
                                                       isSelected ? "bg-blue-600 border-blue-600" : "bg-black/40 border-white/50"
                                                   )}>
                                                       {isSelected && <ICONS.Check className="w-3 h-3 text-white stroke-[3]" />}
                                                   </div>
                                               )}

                                               {/* Normal Hover Overlay (Hidden in Select Mode) */}
                                               {!isSelectionMode && (
                                                   <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity duration-200 flex flex-col justify-end p-2 md:p-3">
                                                        <div className="flex items-center justify-end gap-1 md:gap-2">
                                                            <button 
                                                                onClick={(e) => { 
                                                                    e.preventDefault();
                                                                    e.stopPropagation(); 
                                                                    onToggleFavorite(item.id, !item.isFavorite); 
                                                                }}
                                                                className="p-1.5 md:p-2 bg-white/10 hover:bg-white text-white hover:text-red-500 rounded-full backdrop-blur-md transition-all focus:outline-none focus:ring-2 focus:ring-white"
                                                                aria-label={item.isFavorite ? "Remove from favorites" : "Add to favorites"}
                                                            >
                                                                <ICONS.Heart className={clsx("w-3 h-3", item.isFavorite && "fill-current text-red-500")} />
                                                            </button>

                                                            <button 
                                                                onClick={(e) => { 
                                                                    e.preventDefault();
                                                                    e.stopPropagation(); 
                                                                    onDownload(item.url); 
                                                                }}
                                                                className="p-1.5 md:p-2 bg-white/10 hover:bg-white text-white hover:text-black rounded-full backdrop-blur-md transition-all focus:outline-none focus:ring-2 focus:ring-white"
                                                                title="Download"
                                                                aria-label="Download image"
                                                            >
                                                                <ICONS.Download className="w-3 h-3" />
                                                            </button>
                                                            
                                                            <button 
                                                                onClick={(e) => { 
                                                                    e.preventDefault();
                                                                    e.stopPropagation(); 
                                                                    onPushProcess && onPushProcess(item); 
                                                                }} 
                                                                className="p-1.5 md:p-2 bg-white/10 hover:bg-white text-white hover:text-black rounded-full backdrop-blur-md transition-all focus:outline-none focus:ring-2 focus:ring-white"
                                                                title="Remix"
                                                                aria-label="Remix this image"
                                                            >
                                                                <ICONS.Zap className="w-3 h-3" />
                                                            </button>
                                                            
                                                            <button 
                                                                onClick={(e) => { 
                                                                    e.preventDefault();
                                                                    e.stopPropagation(); 
                                                                    if(confirm('Delete image?')) onRemove(item.id); 
                                                                }} 
                                                                className="p-1.5 md:p-2 bg-white/10 hover:bg-red-600 text-white rounded-full backdrop-blur-md transition-all focus:outline-none focus:ring-2 focus:ring-red-500"
                                                                aria-label="Delete image"
                                                            >
                                                                <ICONS.Trash className="w-3 h-3" />
                                                            </button>
                                                        </div>
                                                   </div>
                                               )}

                                               {/* Always visible favorite badge */}
                                               {!isSelectionMode && item.isFavorite && (
                                                   <div className="absolute top-1 right-1 md:top-2 md:right-2 pointer-events-none">
                                                       <ICONS.Heart className="w-3 h-3 fill-red-500 text-red-500 drop-shadow-md" />
                                                   </div>
                                               )}
                                           </div>
                                       );
                                   })}
                               </div>
                           </section>
                       ))}
                   </div>
               )}
            </div>
       </div>

        {/* BULK ACTIONS FLOATING BAR */}
        <AnimatePresence>
            {isSelectionMode && selectedIds.size > 0 && (
                <motion.div 
                    initial={{ y: 100, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: 100, opacity: 0 }}
                    className="fixed bottom-0 left-0 right-0 p-6 flex justify-center z-[110] pointer-events-none"
                >
                    <div className="bg-zinc-900 border border-zinc-700 rounded-full shadow-2xl flex items-center px-4 py-2 gap-4 pointer-events-auto">
                        <span className="text-white font-bold text-xs">{selectedIds.size} Selected</span>
                        <div className="w-px h-4 bg-zinc-700"></div>
                        
                        <button 
                            onClick={() => handleBulkFav(true)}
                            className="p-2 hover:bg-white/10 rounded-full text-zinc-300 hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-white" 
                            title="Favorite Selected"
                            aria-label="Favorite Selected Items"
                        >
                            <ICONS.Heart className="w-4 h-4" />
                        </button>
                        
                        <button 
                            onClick={handleBulkDelete}
                            className="p-2 hover:bg-red-900/50 rounded-full text-zinc-300 hover:text-red-500 transition-colors focus:outline-none focus:ring-2 focus:ring-red-500"
                            title="Delete Selected"
                            aria-label="Delete Selected Items"
                        >
                            <ICONS.Trash className="w-4 h-4" />
                        </button>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    </div>
  );
};

export default History;
